import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { bindable } from 'aurelia-typed-observable-plugin';
import { noop } from 'app/shared/lib/noop';
import { fireEvents } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';

interface IWizardNavigatorFinalSubmitEvent { item?: any, items?: Array<any> }
export type WizardNavigatorFinalSubmitEvent = CustomEvent<IWizardNavigatorFinalSubmitEvent>;

@autoinject()
export class WizardNavigator extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.object()
    protected collapsible!: EssAccordionCustomAttribute

    @bindable.object()
    public steps!: IWizardStep[];

    @bindable.number({ defaultBindingMode: bindingMode.fromView })
    protected latestStepIndex: number = 0;

    @bindable.function()
    protected cancelAction: Function = noop;

    public currentStep: IWizardStep = this.steps[0];

    @bindable.array()
    protected validationErrors: string[] = [];

    @bindable.boolean()
    protected hideCancelButton: boolean = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(this.steps?.length > 0) {
            this.currentStep = this.steps[0];
            this.latestStepIndex = 0;
        }
    }

    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    public continueToNextSection(): void {
        this.doProcess(async () => {
            if(this.currentStep?.canContinue) {
                this.currentStep.continueStep();
            }

            await this.advanceSection();
        }, 'Error continuing to next step');
    }

    public skipToNextSection(): void {
        this.doProcess(async () => {
            this.currentStep.skipStep();
            await this.advanceSection();
        }, 'Error skipping step');
    }

    private async advanceSection(): Promise<void> {
        if(this.currentStep.closeStep) {
            await this.currentStep.closeStep();
        }

        this.latestStepIndex++;

        while(this.steps[this.latestStepIndex] == null && this.latestStepIndex <= this.steps.length) {
            this.latestStepIndex++;
        }

        if(this.latestStepIndex > this.steps.length) {
            this.latestStepIndex = this.steps.length;
        }

        this.currentStep = this.steps[this.latestStepIndex]
        this.collapsible.open(this.latestStepIndex);
        this.currentStep.openStep();
    }

    public isSectionEnabled(item: Element): boolean {
        if(!this.collapsible || !this.collapsible.calculateIndex) return false;
        const index = this.collapsible.calculateIndex(item);
        return index <= this.latestStepIndex && index >= 0;
    }

    public reset(): void {
        this.latestStepIndex = 0;
        this.currentStep = this.steps[0];
    }

    public openStep(stepIndex: number): void {
        if(!this.steps[stepIndex].openStep) { return; }
        this.steps[stepIndex].openStep();
    }

    public closeStep(stepIndex: number): void {
        if(!this.steps[stepIndex].closeStep) { return; }
        this.steps[stepIndex].closeStep();
    }

    public async finalSubmit(stop: boolean, item?: any, items?: Array<any>): Promise<void> {
        if(!stop) {
            this.currentStep.finalSubmit(this);
            return;
        }

        const detail: IWizardNavigatorFinalSubmitEvent = { item: item, items: items };
        fireEvents(this.$element, 'wizard-navigator-final-submit', detail);
    }

    ////****************** Service Calls ********************************************////

}
